import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import { retrieveFundingRequestEligibility } from "@academy-context/read/application/use-cases/student/funding-request-eligibility-retrieval/retrieve-funding-request-eligibility";
import { retrieveStudentProfile } from "@user-management-context/read/application/use-cases/student/profile-retrieval/retrieve-student-profile";
import { retrieveStudentSummary } from "@academy-context/read/application/use-cases/student/summary-retrieval/retrieve-student-summary";
import { selectStudentProfileRetrieval } from "@user-management-context/read/application/use-cases/student/profile-retrieval/selectors/student-profile-selectors";
import { studentRetrievePendingActions } from "@academy-context/read/application/use-cases/student/pending-actions-retrieval/retrieve-student-pending-actions";
import { selectStudentPendingActions } from "@academy-context/read/application/use-cases/student/pending-actions-retrieval/selectors/student-pending-actions-selectors";
import { educationalAdvisorRetrievePendingActions } from "@academy-context/read/application/use-cases/educational-advisor/pending-actions-retrieval/retrieve-pending-actions";
import { selectEducationalAdvisorPendingActions } from "@academy-context/read/application/use-cases/educational-advisor/pending-actions-retrieval/selectors/pending-actions-selectors";
import { adminRetrievePendingActions } from "@academy-context/read/application/use-cases/admin/pending-actions-retrieval/retrieve-admin-pending-actions";
import { selectAdminPendingActions } from "@academy-context/read/application/use-cases/admin/pending-actions-retrieval/selectors/admin-pending-actions-selectors";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail, SidebarSeparator, useSidebar } from "@components/ui/sidebar";
import { NavHeader } from "@shared-kernel/primary/layout/side-bar/side-bar-header";
import { NavUser } from "@shared-kernel/primary/layout/side-bar/nav-user";
import { AdminSideBar } from "@shared-kernel/primary/layout/side-bar/admin-side-bar";
import { RoleSwitcher } from "@shared-kernel/primary/layout/side-bar/role-switcher";
import { StandardUserSideBar } from "@shared-kernel/primary/layout/side-bar/standard-user-side-bar";
import { retrieveTeacherProfile } from "@user-management-context/read/application/use-cases/teacher/teacher-profile-retrieval/retrieve-teacher";
import { retrieveProviderProfile } from "@user-management-context/read/application/use-cases/provider/provider-profile-retrieval/retrieve-provider";
import type { UserVM } from "@shared-kernel/application/ports/shared/shared-repository";
import { impersonateUser } from "@user-management-context/write/application/use-cases/impersonate-user/impersonate-user";

interface Props {
  user: UserVM;
  logout: () => void;
}

export const SideBar = ({ user, logout }: Props) => {
  const dispatch = useAppDispatch();
  const { activeRole } = useSession();
  const { data: student } = useAppSelector(selectStudentProfileRetrieval);
  const studentPendingActions = useAppSelector(selectStudentPendingActions);
  const educationalAdvisorPendingActions = useAppSelector(selectEducationalAdvisorPendingActions);
  const adminPendingActions = useAppSelector(selectAdminPendingActions);
  const { state } = useSidebar();

  const isStudent = activeRole === ROLES.STUDENT;
  const isTeacher = activeRole === ROLES.TEACHER;
  const isProvider = activeRole === ROLES.PROVIDER;
  const isAdmin = user.isAdmin;

  useEffect(() => {
    if (isStudent) {
      dispatch(retrieveStudentProfile());
      dispatch(retrieveFundingRequestEligibility());
      dispatch(studentRetrievePendingActions());
      dispatch(retrieveStudentSummary());
    }
  }, [dispatch, isStudent]);

  useEffect(() => {
    if (isTeacher) {
      // This call is needed to fetch default price and the payout mode
      dispatch(retrieveTeacherProfile());
      dispatch(educationalAdvisorRetrievePendingActions());
    }
  }, [dispatch, isTeacher]);

  useEffect(() => {
    if (isAdmin) {
      dispatch(adminRetrievePendingActions());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (isProvider) {
      dispatch(retrieveProviderProfile());
      dispatch(educationalAdvisorRetrievePendingActions());
    }
  }, [dispatch, isProvider]);

  const onLogout = () => {
    logout();
  };

  const onImpersonate = (userId: string, activeRole: ROLES) => {
    dispatch(impersonateUser({ targetUserId: userId, activeRole }));
  };

  const NavBar = isAdmin ? (
    <AdminSideBar adminPendingActions={adminPendingActions} />
  ) : (
    <StandardUserSideBar
      role={activeRole}
      student={student}
      studentPendingActions={studentPendingActions}
      educationalAdvisorEvaluationPendingCount={educationalAdvisorPendingActions.evaluations.pendingCount}
      educationalAdvisorBatchPendingCount={educationalAdvisorPendingActions.batches.pendingCount}
    />
  );

  return (
    <Sidebar collapsible="icon" variant="sidebar">
      <SidebarHeader className="py-5">
        <NavHeader user={user} state={state} />
      </SidebarHeader>
      <SidebarContent className="gap-0">
        <RoleSwitcher />
        {NavBar}
      </SidebarContent>
      <SidebarSeparator />
      <SidebarFooter>
        <NavUser user={user} onLogout={onLogout} onImpersonate={onImpersonate} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
